/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import PropTypes from 'prop-types';
import { LabelWithRequired, joinDataTestId } from '@motili/ui';

import { generateDefaultDataTestId } from 'common/utils/Utils';
import MotiliTextButton from 'common/components/buttons/MotiliTextButton';

import styles from 'common/components/form/FormTextView.module.scss';
import { useUI } from 'common/context/UIContext';

const FormTextView = ({
    label,
    value,
    button,
    showAsLink,
    urlDestination,
    style,
    labelStyle,
    messageStyle,
    bypassPreTag,
    linkTarget,
    required,
    emptyDisplay = '',
    dataTestId = '',
    preserveCase,
}) => {
    const { isLegacyUI } = useUI();
    const isDisplayAsLegacyUI = !preserveCase && isLegacyUI;
    const transformedEmptyDisplay = isDisplayAsLegacyUI
        ? emptyDisplay || '<BLANK>'
        : emptyDisplay;
    return (
        <>
            <div className={styles.labelContainer}>
                <div>
                    {label &&
                        (labelStyle ? (
                            <label data-test-id='label' style={labelStyle}>
                                {isDisplayAsLegacyUI
                                    ? label.toUpperCase()
                                    : label}
                                {required && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                            </label>
                        ) : (<LabelWithRequired
                            label={
                                isDisplayAsLegacyUI
                                    ? label.toUpperCase()
                                    : label
                            }
                            required={required}
                            dataTestId={dataTestId}
                        />
                    ))}
                </div>
                {button && (
                    <MotiliTextButton
                        disabled={button.disabled}
                        text={button.text}
                        onClick={button.onClick}
                        color={button.color}
                        data-test-id={dataTestId}
                    />
                )}
            </div>
            {showAsLink && showAsLink === true && (
                <div className={styles.value} style={style}>
                    {value ? (
                        <a
                            target={linkTarget || '_self'}
                            href={urlDestination}
                            data-test-id={
                                joinDataTestId([dataTestId, 'link_value']) ||
                                generateDefaultDataTestId(label, 'link')
                            }
                        >
                            {value}
                        </a>
                    ) : (
                        transformedEmptyDisplay
                    )}
                </div>
            )}

            {(bypassPreTag === undefined || bypassPreTag === false) &&
                (!showAsLink || showAsLink === false) && (
                    <div className={styles.value} style={style}>
                        <pre
                            data-test-id={joinDataTestId([dataTestId, 'value'])}
                            className={styles.message}
                            style={messageStyle}
                        >
                            {value === 0
                                ? value
                                : value || transformedEmptyDisplay}
                        </pre>
                    </div>
                )}
            {bypassPreTag && (!showAsLink || showAsLink === false) && (
                <div className={styles.value} style={style}>
                    <div data-test-id={joinDataTestId([dataTestId, 'value'])}>
                        {value === 0 ? value : value || transformedEmptyDisplay}
                    </div>
                </div>
            )}
        </>
    );
};

FormTextView.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
    button: PropTypes.object,
    style: PropTypes.object,
    labelStyle: PropTypes.object,
    messageStyle: PropTypes.object,
    showAsLink: PropTypes.bool,
    urlDestination: PropTypes.string,
    bypassPreTag: PropTypes.bool,
    linkTarget: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    emptyDisplay: PropTypes.string,
    dataTestId: PropTypes.string,
    preserveCase: PropTypes.bool,
};

export default FormTextView;
